import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import VariantPicker from "../shared/VariantPicker";

// Mirrors BikeValidation.js's bikeCreate()/bikeEdit() chains exactly - a
// much smaller surface than Car's: just registrationNo, variantId (scoped
// to the bike catalog via VariantPicker), an optional ownerId (create only)
// and an optional color. Bike carries no fuelType/transmissionType/year/
// features/fasTag at all.
const BikeFormModal = ({ mode = "create", bike, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);

    const [registrationNo, setRegistrationNo] = useState(bike?.registrationNo || "");
    const [variantId, setVariantId] = useState(bike?.variantId || "");
    const [changingVariant, setChangingVariant] = useState(mode === "create");
    const [ownerId, setOwnerId] = useState(bike?.ownerId || "");
    const [color, setColor] = useState(bike?.color || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async () => {
        const reg = registrationNo.trim();
        if (!reg) {
            toast.error("Registration number is required");
            return;
        }
        if (mode === "create" && !variantId) {
            toast.error("Select a variant");
            return;
        }

        setSaving(true);
        const payload = {
            registrationNo: reg,
            ...(changingVariant && variantId ? { variantId } : {}),
            ...(mode === "create" && ownerId.trim() ? { ownerId: ownerId.trim() } : {}),
            ...(color.trim() ? { color: color.trim() } : {}),
        };

        const response = mode === "edit"
            ? await Handler({ method: "patch", url: `/admin/bike/${bike.bikeId}`, data: payload })
            : await Handler({ method: "post", url: "/admin/bike", data: payload });

        if (response?.success) {
            toast.success(response?.data?.message || (mode === "edit" ? "Bike updated" : "Bike registered"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the bike"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "edit" ? "Edit Bike" : "Register Bike"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Registration number</label>
                                <input type="text" className="form-control text-uppercase" value={registrationNo} onChange={(e) => setRegistrationNo(e.target.value)} placeholder="e.g. TN09AB1234" maxLength={10} />
                            </div>
                            {mode === "create" ? (
                                <div className="col-12 col-md-6">
                                    <label className="form-label">Owner ID (optional)</label>
                                    <input type="text" className="form-control" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} placeholder="Defaults to site fleet" />
                                </div>
                            ) : null}
                            <div className="col-12 col-md-6">
                                <label className="form-label">Color (optional)</label>
                                <input type="text" className="form-control" value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Red" />
                            </div>

                            <div className="col-12">
                                <label className="form-label d-block">Variant</label>
                                {!changingVariant ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge rounded-pill text-bg-light border">{bike?.variant?.variantName || variantId}</span>
                                        <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setChangingVariant(true)}>Change variant</button>
                                    </div>
                                ) : (
                                    <VariantPicker brandType="bikes" onSelect={setVariantId} />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Register bike"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BikeFormModal;
